/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { JobVariationStatus } from '@gh/builder-shared-data';
import {
  AbstractSnapshotFormComponent,
  DateTimeTypes,
  EnumDictionary,
  FormGroupSchemaConfig,
  FormState,
  FormStateBuilder,
  FormStateSnapshot,
  getFormStateSnapshotValue,
  getRouteInitData,
  NumberTypes, TextTypes,
  updateSchemaWithChildren,
  whenCreateMode,
  whenCreateOrEditMode,
} from '@gh/common-shared';
import { AutocompleteLoadMoreEvent, getControlValue } from '@gh/core-ui';
import { currentNormalizedDate, isNotNil, normalizeDate } from '@gh/core-util';

import { isNil } from 'lodash';

import { JobCoDetailsBusinessService } from '../../../resources/job-co-details-business.service';
import { JobCoDetailsDrawStage, JobCoDetailsMasterInfo } from '../../../resources/job-co-details.model';

@Component({
  selector: 'gh-job-co-details-top-form',
  template: require('./job-co-details-top-form.component.html'),
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobCoDetailsTopFormComponent extends AbstractSnapshotFormComponent<JobCoDetailsMasterInfo>
  implements OnInit {
  @Input() hasChanges: boolean;
  @Input() drawRuleStages: JobCoDetailsDrawStage[];

  @Output() reverse = new EventEmitter<void>();
  @Output() preview = new EventEmitter<void>();
  @Output() changeRound = new EventEmitter<void>();

  statusDict: EnumDictionary<JobVariationStatus>;

  constructor(private route: ActivatedRoute,
              private formStateBuilder: FormStateBuilder,
              private business: JobCoDetailsBusinessService) {
    super();

    this.isDateAllowed = this.isDateAllowed.bind(this);
  }

  get canReverse(): boolean {
    const status = getControlValue(this.form.group, 'status');
    const isStatusChanged = getControlValue(this.form.group, 'isStatusChanged');
    return this.form.editMode && !isStatusChanged && status === JobVariationStatus.Approved;
  }

  get canPreview(): boolean {
    return !this.form.createMode && isNotNil(getControlValue(this.form.group, 'producedDate')) && this.hasChanges;
  }

  get displayDrawnLabel(): boolean {
    const status = getControlValue(this.form.group, 'status');
    const isClaimed = getControlValue(this.form.group, 'isClaimed');
    return (this.form.editMode || this.form.viewMode) && status === JobVariationStatus.Approved && isClaimed;
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.statusDict = getRouteInitData(this.route)['statusDict'];
  }

  onReverse(): void {
    this.reverse.emit();
  }

  onPreview(): void {
    this.preview.emit();
  }

  onChangeRound(): void {
    this.changeRound.emit();
  }

  loadPriceBook(event: AutocompleteLoadMoreEvent): void {
    event.load(this.business.findAllPriceBooksByName(event.query));
  }

  isDateAllowed(date: Date): boolean {
    return !!date && date !== null ? normalizeDate(date) <= currentNormalizedDate() : false;
  }

  protected buildForm(snapshot: FormStateSnapshot<JobCoDetailsMasterInfo>): FormState<JobCoDetailsMasterInfo> {
    const masterInfo = getFormStateSnapshotValue(snapshot);

    return this.formStateBuilder.buildPlain({
      mode: this.mode,
      snapshot,
      ...this.getFormSchemaConfig(masterInfo),
    });
  }

  protected afterRestored(snapshot: FormStateSnapshot<JobCoDetailsMasterInfo>): void {
    updateSchemaWithChildren(this.form.group, this.getFormSchemaConfig(getFormStateSnapshotValue(snapshot)));
  }

  private getFormSchemaConfig(masterInfo: JobCoDetailsMasterInfo): FormGroupSchemaConfig {
    return {
      enabled: whenCreateOrEditMode(),
      children: {
        description: {
          required: true,
          type: TextTypes.ChangeOrderName,
          enabled: masterInfo.status === JobVariationStatus.Quote && isNil(masterInfo.producedDate),
        },
        timeExtensionDays: {
          enabled: masterInfo.status === JobVariationStatus.Quote && isNil(masterInfo.producedDate),
          type: NumberTypes.Days,
        },
        priceBook: {
          required: true,
          enabled: whenCreateMode(),
        },
        memo: {
          enabled: masterInfo.status === JobVariationStatus.Quote && isNil(masterInfo.producedDate),
        },
        drawRuleId: void 0,
        drawRuleName: { enabled: false },
        drawRuleStage: {
          visible: !masterInfo.isLendersSchedule,
          required: true,
          enabled: !masterInfo.isClaimed && (<JobVariationStatus[]>[
            JobVariationStatus.Quote,
            JobVariationStatus.Approved,
          ]).includes(masterInfo.status),
        },
        invoiceDate: { required: true, type: DateTimeTypes.ShortDate },
        status: { enabled: false },
        isClaimed: void 0,
        isLendersSchedule: void 0,
        isStatusChanged: void 0,
        producedDate: void 0,
        isRounded: {
          enabled: masterInfo.status === JobVariationStatus.Quote && isNil(masterInfo.producedDate),
        },
      },
    };
  }
}
